import assetsConfig, {
	lightTheme,
	darkTheme,
	defaultColors,
	lightThemeColors,
	gradients,
	hoverColors,
} from "./assetsConfig";
import { GlobalStyles } from "./globalStyles";
import { ThemeProvider } from "styled-components";
import { createContext, Suspense, lazy, ComponentType } from "react";
import {
	BrowserRouter as Router,
	Switch,
	Route,
	BrowserRouter,
	Redirect,
	useLocation,
} from "react-router-dom";
import Register from "./views/Register/Register";

import configureValidations from "./Validations";
import { claim } from "./components/Auth/auth.models";
import { useEffect, useState } from "react";
import AuthenticationContext from "./components/Auth/AuthenticationContext";
import { getClaims, refreshTokenIfNeeded } from "./components/Auth/handleJWT";
import confugureInterceptor from "./utils/httpInterceptors";
import Authorized from "./components/Auth/Authorized";
import RedirectToLandingPage from "./utils/RedirectToRoot";
import Logout from "./utils/Logout";
import { TransactionType } from "./views/Checkout/OrderInterfaces";
import ResetPassword from "./views/Login/ResetPassword";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import * as FullStory from "@fullstory/browser";
import Loading, { LoadingOverlay } from "./components/Loading/Loading";

import { NewModal, NewModalProps } from "./components/Modal/Modal";
import { CreditType } from "./views/Admin/Program/ProgramInterfaces";
import { RootState } from "./store";
import Alerts from "./components/Alert/Alerts";
import useHelpWidget from "./hooks/useHelpWidget";
import { useSelector } from "react-redux";
import { useNotifications } from "./hooks/useNotifications";
import AssetsLoader from "./utils/AssetsLoader";
import FooterWrapper from "./components/Footer/Footer";
import RouteErrorBoundary from "./components/ErrorBoundary/RouteErrorBoundary";

// Keep eager — small / critical path + entry form (frequent use, tab navigation)
import Login from "./views/Login/Login";
import EntrantPrograms from "./views/EntrantProgram/EntrantProgram";
import EditEntry from "./views/Entries/EditEntry";

const AdminRoutes = lazy(() => import("./AdminRoutes"));

const PageFallback = () => (
	<Loading fullScreen={false} showLogo={false} text="Loading page..." />
);

type AppRouteConfig = {
	path: string;
	component: ComponentType;
	exact?: boolean;
	feature?: string;
	notAuthorized?: "login" | "redirect-home";
};

const appRoutes: AppRouteConfig[] = [
	{
		path: "/judge/:juryId/:entrySetId",
		exact: true,
		feature: "Judging",
		component: lazy(() => import("./views/Judging/JudgingGallery")),
	},
	{
		path: "/judge/:juryId/:entrySetId/:batchId/:entryId/:voteId",
		exact: true,
		feature: "Judging",
		component: lazy(() => import("./views/Judging/EntryDetail/EntryDetail")),
	},
	{
		path: "/media-viewer/:juryId",
		exact: true,
		feature: "Judging",
		notAuthorized: "redirect-home",
		component: lazy(
			() => import("./views/Admin/Judging/LiveTools/MediaViewer"),
		),
	},
	{
		path: "/programs/:programId",
		component: lazy(() => import("./views/EntrantProgram/EntrantSubprogram")),
	},
	{
		path: "/judge/:juryId",
		exact: true,
		feature: "Judging",
		component: lazy(() => import("./views/Judging/JudgeEntrySets")),
	},
	{
		path: "/user-info",
		exact: true,
		component: lazy(() =>
			import("./views/Admin/User/User").then((module) => ({
				default: () => <module.default tabIndex={0} />,
			})),
		),
	},
	{
		path: "/company-info",
		exact: true,
		component: lazy(() =>
			import("./views/Admin/User/User").then((module) => ({
				default: () => <module.default tabIndex={1} />,
			})),
		),
	},
	{
		path: "/edit-entry/:tab?",
		component: EditEntry,
	},
	{
		path: "/payment/Credit",
		exact: true,
		feature: "Commerce",
		component: lazy(() =>
			import("./views/Checkout/Payment").then((module) => ({
				default: () => (
					<module.default transactionType={TransactionType.Credit} />
				),
			})),
		),
	},
	{
		path: "/payment/Check",
		exact: true,
		feature: "Commerce",
		component: lazy(() =>
			import("./views/Checkout/Payment").then((module) => ({
				default: () => (
					<module.default transactionType={TransactionType.Check} />
				),
			})),
		),
	},
	{
		path: "/payment/Bank",
		exact: true,
		feature: "Commerce",
		component: lazy(() =>
			import("./views/Checkout/Payment").then((module) => ({
				default: () => (
					<module.default transactionType={TransactionType.Bank} />
				),
			})),
		),
	},
	{
		path: "/payment/Paymentz",
		exact: true,
		feature: "Commerce",
		component: lazy(() =>
			import("./views/Checkout/Payment").then((module) => ({
				default: () => (
					<module.default transactionType={TransactionType.Paymentz} />
				),
			})),
		),
	},
	{
		path: "/payment/Offline",
		exact: true,
		feature: "Commerce",
		component: lazy(() =>
			import("./views/Checkout/Payment").then((module) => ({
				default: () => (
					<module.default transactionType={TransactionType.Offline} />
				),
			})),
		),
	},
	{
		path: "/checkout/complete",
		feature: "Commerce",
		component: lazy(() => import("./views/Checkout/PaymentzComplete")),
	},
	{
		path: "/entries/draft-entries",
		exact: true,
		component: lazy(() =>
			import("./views/Checkout/Checkout").then((module) => ({
				default: () => <module.default tab="draft-entries" />,
			})),
		),
	},
	{
		path: "/entries/cart",
		exact: true,
		component: lazy(() =>
			import("./views/Checkout/Checkout").then((module) => ({
				default: () => <module.default tab="cart" />,
			})),
		),
	},
	{
		path: "/entries/orders",
		exact: true,
		feature: "Commerce",
		component: lazy(() =>
			import("./views/Checkout/Checkout").then((module) => ({
				default: () => <module.default tab="orders" />,
			})),
		),
	},
	{
		path: "/entries/entries-search",
		exact: true,
		component: lazy(() =>
			import("./views/Checkout/Checkout").then((module) => ({
				default: () => <module.default tab="entries-search" />,
			})),
		),
	},
	{
		path: "/entries",
		exact: true,
		component: lazy(() => import("./views/Checkout/Checkout")),
	},
	{
		path: "/scoreboard/:juryId",
		exact: true,
		feature: "Judging",
		component: lazy(() => import("./views/Judging/Scoreboard/Scoreboard")),
	},
	{
		path: "/credits/company",
		exact: true,
		component: lazy(() =>
			import("./views/Admin/Credits/CreditLibrary").then((module) => ({
				default: () => <module.default creditType={CreditType.Company} />,
			})),
		),
	},
	{
		path: "/credits/individual",
		exact: true,
		component: lazy(() =>
			import("./views/Admin/Credits/CreditLibrary").then((module) => ({
				default: () => <module.default creditType={CreditType.Individual} />,
			})),
		),
	},
	{
		path: "/media",
		exact: true,
		component: lazy(() => import("./views/Admin/Media/MediaLibrary")),
	},
	{
		path: "/tags",
		exact: true,
		component: lazy(() => import("./views/TagLibrary/TagLibrary")),
	},
	{
		path: "/judgingredirect/:entryId",
		exact: true,
		feature: "Judging",
		component: lazy(() => import("./views/Admin/Judging/JudgingRedirect")),
	},
];

FullStory.init({ orgId: "o-1JY9MT-na1" });

configureValidations();
confugureInterceptor();

export const LoadingContext = createContext<{
	loadingMessage: string | null;
	setLoadingMessage: React.Dispatch<React.SetStateAction<string | null>>;
}>({
	loadingMessage: null,
	setLoadingMessage: () => {},
});

export const ModalContext = createContext<{
	modal: NewModalProps | null;
	setModal: React.Dispatch<React.SetStateAction<NewModalProps | null>>;
}>({
	modal: null,
	setModal: () => {},
});

function App() {
	useHelpWidget();
	const location = useLocation();
	const { settings, loading } = useSelector(
		(state: RootState) => state.settings,
	);

	const [claims, setClaims] = useState<claim[]>(getClaims());
	const [hasInitialized, setHasInitialized] = useState(false);

	useNotifications(claims);

	useEffect(() => {
		const emailClaim = claims.find((claim: claim) => claim.name === "email");

		if (emailClaim) {
			const email = emailClaim.value;

			FullStory.identify(email, {
				displayName: email,
				email: email,
			});
		} else {
			FullStory.anonymize();
		}
	}, [claims]);

	useEffect(() => {
		async function initialize() {
			const updatedClaims = await refreshTokenIfNeeded();
			if (updatedClaims) {
				setClaims(updatedClaims);
			}
			setHasInitialized(true);
		}
		if (!hasInitialized) {
			setTimeout(() => {
				initialize();
			}, 500);
		}
	}, [hasInitialized]);

	// useEffect(() => {
	//   const prefetchImage = (url: string) => {
	//     const img = new Image();
	//     img.src = url;
	//   };

	//   prefetchImage(config.assets.logos.local);
	//   prefetchImage(config.assets.loading.primary);
	// }, []);

	const [theme, setTheme] = useState("light");
	const toggleTheme = () => {
		if (theme === "light") {
			setTheme("dark");
		} else {
			setTheme("light");
		}
	};
	const [modal, setModal] = useState<NewModalProps | null>(null);
	const [loadingMessage, setLoadingMessage] = useState<null | string>(null);

	// inject theme into CSS variables
	useEffect(() => {
		const themeVariables = {
			...defaultColors,
			...lightThemeColors,
			...gradients,
			...hoverColors,
		};

		const root = document.documentElement;

		Object.keys(themeVariables).forEach((color) => {
			root.style.setProperty(`--${color}`, (themeVariables as ITheme)[color]);
		});
	}, [theme]);

	return (
		<ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
			<AssetsLoader>
				<Alerts />

				<LoadingOverlay
					show={loadingMessage !== null}
					message={loadingMessage}
				/>
				<GlobalStyles />
				<DndProvider backend={HTML5Backend}>
					<AuthenticationContext.Provider
						value={{ claims, update: setClaims }}
					>
						<ModalContext.Provider value={{ modal, setModal }}>
							{modal !== null && <NewModal {...modal} />}
							<LoadingContext.Provider
								value={{ loadingMessage, setLoadingMessage }}
							>
								<FooterWrapper>
									<RouteErrorBoundary resetKey={location.pathname}>
										<Suspense fallback={<PageFallback />}>
											<Switch location={location}>
												{/*               <Route exact path="/test-components">
		<Button
		  className="absolute bottom-[2rem] left-[2rem]"
		  onClick={() => toggleTheme()}
		>
		  Toggle Theme
		</Button>
		<TestComponents />
	  </Route>{" "} */}
												{/* <Route exact path="/icon-display">
		<IconDisplay />
	  </Route> */}
												{/* <Route exact path="/date">
									<TestDatePickerPage />
								</Route> */}

												<Route exact path="/logout">
													<Logout />
												</Route>
												<Route exact path="/login">
													<Authorized
														settings={settings}
														loading={loading}
														authorized={
															claims.find(
																(c: claim) =>
																	c.name === "role" && c.value === "admin",
															) ? (
																<Redirect to={"/library/entries"} />
															) : (
																<Redirect to="/" />
															)
														}
														notAuthorized={<Login />}
													/>
												</Route>
												<Route exact path="/ResetPassword">
													<ResetPassword />
												</Route>
												<Route exact path="/">
													<Authorized
														settings={settings}
														loading={loading}
														authorized={<EntrantPrograms />}
														notAuthorized={<Login />}
													/>
												</Route>
												<Route exact path="/register">
													<Register />
												</Route>
												{appRoutes.map((route) => {
													const RouteComponent = route.component;
													return (
														<Route
															key={route.path}
															path={route.path}
															exact={route.exact}
														>
															<Authorized
																settings={settings}
																loading={loading}
																feature={route.feature}
																authorized={<RouteComponent />}
																notAuthorized={
																	route.notAuthorized === "redirect-home" ? (
																		<Redirect to="/" />
																	) : (
																		<Login />
																	)
																}
															/>
														</Route>
													);
												})}
												<Route exact path="/company">
													<Authorized
														settings={settings}
														loading={loading}
														authorized={<Redirect to="/" />}
														notAuthorized={<Login />}
													/>
												</Route>
												<Route exact path="/programs">
													<Authorized
														settings={settings}
														loading={loading}
														authorized={<EntrantPrograms />}
														notAuthorized={<Login />}
													/>
												</Route>
												<Route exact path="/new-entry">
													<Authorized
														settings={settings}
														loading={loading}
														authorized={<EntrantPrograms />}
														notAuthorized={<Login />}
													/>
												</Route>
												<Route>
													<Authorized
														settings={settings}
														loading={loading}
														role="admin"
														authorized={
															<AdminRoutes
																settings={settings}
																loading={loading}
																location={location}
															/>
														}
														notAuthorized={<RedirectToLandingPage />}
													/>
												</Route>
											</Switch>
										</Suspense>
									</RouteErrorBoundary>
								</FooterWrapper>
							</LoadingContext.Provider>
						</ModalContext.Provider>
					</AuthenticationContext.Provider>
				</DndProvider>
			</AssetsLoader>
		</ThemeProvider>
	);
}

export default App;

interface ITheme {
	[key: string]: string;
}
